import type { TranslationKey } from "@/shared/i18n";

/**
 * BrowserRouter paths (SPEC §9 — real, shareable, indexable URLs).
 * `labelKey` is an i18n key, never a literal string: nav labels are
 * user-facing and must go through `t()`.
 */
export enum RouteNames {
  landing = "landing",
  parcels = "parcels",
  parcelDetail = "parcelDetail",
  checkoutReturn = "checkoutReturn",
  myFarm = "myFarm",
  admin = "admin",
  terms = "terms",
}

export interface IRouteStructure {
  path: string;
  /** Path prefix for building links to a parameterised route. */
  name?: string;
  labelKey: TranslationKey;
  /** Show in the primary site navigation. */
  inNav?: boolean;
  /** Requires an authenticated session. */
  requiresAuth?: boolean;
  /** Requires `SessionUser.isAdmin`. */
  requiresAdmin?: boolean;
}

export const appRoutes: { [key in RouteNames]: IRouteStructure } = {
  [RouteNames.landing]: {
    path: "/",
    labelKey: "nav.home",
    inNav: true,
  },
  // The browser paths are `/memberships`, but the enum keys, the `Parcel`
  // type and the `/api/parcels/*` endpoints keep their names: the API is a
  // deployed contract and renaming it would be a server release, not a copy
  // change. The pre-rename `/parcels` URLs are not redirected — the site has
  // not launched, so none are in the wild.
  [RouteNames.parcels]: {
    path: "/memberships",
    labelKey: "nav.parcels",
    inNav: true,
  },
  [RouteNames.parcelDetail]: {
    path: "/memberships/:vaultId",
    name: "/memberships/",
    labelKey: "parcel.catalogueTitle",
  },
  [RouteNames.checkoutReturn]: {
    path: "/checkout/return",
    labelKey: "checkout.returnTitle",
    requiresAuth: true,
  },
  [RouteNames.myFarm]: {
    path: "/my-farm",
    labelKey: "nav.myFarm",
    inNav: true,
    requiresAuth: true,
  },
  [RouteNames.admin]: {
    path: "/admin",
    labelKey: "nav.admin",
    requiresAuth: true,
    requiresAdmin: true,
  },
  // Public and indexable on purpose: Stripe's review looks for the terms of
  // sale on the business website, and EU pre-contractual duties mean a buyer
  // has to be able to read them before ordering, not only after.
  [RouteNames.terms]: {
    path: "/terms",
    labelKey: "footer.terms",
  },
};

/** `parcelPath('abc')` → `/memberships/abc`. */
export const parcelPath = (vaultId: string): string =>
  `${appRoutes.parcelDetail.name}${vaultId}`;

/** Routes rendered in the header, in order. */
export const navRoutes: IRouteStructure[] = [
  appRoutes[RouteNames.landing],
  appRoutes[RouteNames.parcels],
  appRoutes[RouteNames.myFarm],
];
